import {Point} from '@logistics-frontend/core/types'
import {SuggestObject} from '@yandex-taxi/js-integration-api'

import type {LngLat, LngLatBounds, Vec2} from './types'

export const isBoundsSame = (bounds: LngLatBounds): boolean =>
  bounds[0][0] === bounds[1][0] && bounds[0][1] === bounds[1][1]

export const isLngLat = (arr: (number | undefined)[]): arr is LngLat => arr.length >= 2 && !!arr[0] && !!arr[1]

export const getPlacemarkCoords = (addressValue: SuggestObject) =>
  addressValue.pos.split(',').map(val => parseFloat(val)) as Point

export const lngLatsToBounds = (lngLats: LngLat[], addMargins: boolean = false): LngLatBounds | undefined => {
  if (lngLats.length === 0) {
    return undefined
  }
  const nonNormalizedBounds = lngLats.reduce<LngLatBounds>(
    (acc, [longitude, latitude]) => [
      [Math.min(acc[0][0], longitude!), Math.max(acc[0][1], latitude!), 0.5],
      [Math.max(acc[1][0], longitude!), Math.min(acc[1][1], latitude!), 0.5],
    ],
    [
      //top      left
      [Infinity, -Infinity],
      //bottom   right
      [-Infinity, Infinity],
    ],
  )
  const lngAmplitude = nonNormalizedBounds[1][0] - nonNormalizedBounds[0][0]
  const latAmplitude = nonNormalizedBounds[0][1] - nonNormalizedBounds[1][1]
  const k = 0.2 * Number(addMargins)
  return [
    [nonNormalizedBounds[0][0] - k * lngAmplitude, nonNormalizedBounds[0][1] + k * latAmplitude],
    [nonNormalizedBounds[1][0] + k * lngAmplitude, nonNormalizedBounds[1][1] - k * latAmplitude],
  ]
}

export const isPointInsideMapBounds = (
  coords: Vec2,
  mapBounds: [top: number, bottom: number, left: number, right: number],
) => {
  const [top, bottom, left, right] = mapBounds

  return coords.y < bottom && coords.y > top && coords.x > left && coords.x < right
}

export const lngLatToPoint = (lngLat: LngLat): Point => {
  return [lngLat[0], lngLat[1]]
}

export const getHalfViewportSize = (pixel: Vec2, zoom: number) => {
  // World at 0 zoom is always 256x256 pixels.
  const worldPixelSize = 256

  function divv(a: Vec2, b: Vec2): Vec2 {
    return {x: a.x / b.x, y: a.y / b.y}
  }
  function convertPixelSizeToWorldSize(pixel: Vec2, zoom: number): Vec2 {
    const scale = (2 ** zoom / 2) * worldPixelSize
    return divv(pixel, {x: scale, y: -scale})
  }
  function divn(v: Vec2, n: number): Vec2 {
    return {x: v.x / n, y: v.y / n}
  }

  return divn(convertPixelSizeToWorldSize(pixel, zoom), 2)
}
